import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import {
    LuPencil,
    LuTrash,
    LuPlus,
    LuArrowDownRight,
    LuArrowUpRight,
    LuPackage,
} from "react-icons/lu";
import { useDataTable } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { FilterData } from "./components";
import { formatThousands, formatDate } from "@/utils";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    date_from: "",
    date_to: "",
};

const StockAdjustmentData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("stock-adjustment.edit") || hasPermission("stock-adjustment.delete");
    const table = useDataTable(
        "stock-adjustments/data",
        "stock-adjustments/{id}/delete",
        {},
        initialFilters,
    );

    // Filter labels
    const filterLabels = {
        date_from: {
            label: "Dari Tanggal",
            getValueLabel: (val) => formatDate(val),
        },
        date_to: {
            label: "Sampai Tanggal",
            getValueLabel: (val) => formatDate(val),
        },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Batch ID", skeletonWidth: "w-32" },
        { name: "Tanggal", skeletonWidth: "w-24" },
        { name: "Jumlah Item", skeletonWidth: "w-20" },
        { name: "Masuk", skeletonWidth: "w-20" },
        { name: "Keluar", skeletonWidth: "w-20" },
        { name: "Keterangan", skeletonWidth: "w-40" },
        ...(hasAnyAction ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }] : []),
    ];

    const columns = [
        { key: "no", label: "No.", width: 50 },
        { key: "batch_id", label: "Nomor Batch" },
        { key: "date", label: "Tanggal" },
        { key: "items", label: "Jumlah Item" },
        {
            key: "in",
            label: (
                <div className="flex items-center justify-end gap-1">
                    <LuArrowDownRight size={14} className="text-success" />
                    Masuk
                </div>
            ),
            align: "end",
        },
        {
            key: "out",
            label: (
                <div className="flex items-center justify-end gap-1">
                    <LuArrowUpRight size={14} className="text-danger" />
                    Keluar
                </div>
            ),
            align: "end",
        },
        { key: "description", label: "Keterangan" },
        ...(hasAnyAction
            ? [{ key: "actions", label: "Aksi", align: "end" }]
            : []),
    ];

    const renderCell = (item, columnKey) => {
        switch (columnKey) {
            case "no":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {(table.pagination.page - 1) *
                            table.pagination.perPage +
                            table.items.indexOf(item) +
                            1}
                        .
                    </ThemedText>
                );
            case "batch_id":
                return (
                    <Chip
                        size="sm"
                        variant="flat"
                        color="primary"
                        className="font-mono text-xs"
                    >
                        {item.relation_table_id}
                    </Chip>
                );
            case "date":
                return (
                    <ThemedText size="sm">{formatDate(item.date)}</ThemedText>
                );
            case "items":
                return (
                    <Chip size="sm" variant="flat">
                        {item.item_count} produk
                    </Chip>
                );
            case "in":
                return item.total_in > 0 ? (
                    <ThemedText size="sm">
                        +{formatThousands(item.total_in)}
                    </ThemedText>
                ) : (
                    <ThemedText size="sm" variant="secondary">
                        -
                    </ThemedText>
                );
            case "out":
                return item.total_out > 0 ? (
                    <ThemedText
                        size="sm"
                        className="text-danger font-medium"
                    >
                        -{formatThousands(item.total_out)}
                    </ThemedText>
                ) : (
                    <ThemedText size="sm" variant="secondary">
                        -
                    </ThemedText>
                );
            case "description":
                return (
                    <ThemedText
                        size="xs"
                        variant="caption"
                        className="line-clamp-1"
                    >
                        {item.description || "-"}
                    </ThemedText>
                );
            case "actions":
                return (
                    <div className="relative flex items-center justify-end gap-2">
                        {hasPermission("stock-adjustment.edit") && (
                            <Tooltip content="Edit Penyesuaian">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    as={Link}
                                    to={`/stock-adjustment/${item.relation_table_id}/edit`}
                                >
                                    <LuPencil
                                        size={14}
                                        className="text-default-500"
                                    />
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission("stock-adjustment.delete") && (
                            <Tooltip content="Hapus Penyesuaian">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    color="danger"
                                    variant="light"
                                    onPress={() =>
                                        table.deleteState.openDeleteModal(
                                            item.relation_table_id,
                                        )
                                    }
                                >
                                    <LuTrash size={14} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Penyesuaian Stok"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Penyesuaian Stok" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("stock-adjustment.create") && (
                        <Button
                            as={Link}
                            to="/stock-adjustment/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Penyesuaian
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData table={table} initialFilters={initialFilters} />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Penyesuaian"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada penyesuaian yang cocok dengan filter."
                                    : "Mulai dengan menambahkan penyesuaian stok baru."
                            }
                            icon={LuPackage}
                        />
                    ) : (
                        <Table
                            aria-label="Tabel Penyesuaian Stok"
                            shadow="none"
                        >
                            <TableHeader columns={columns}>
                                {(column) => (
                                    <TableColumn
                                        key={column.key}
                                        align={column.align}
                                        width={column.width}
                                    >
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.relation_table_id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {renderCell(item, columnKey)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal
                table={table}
                title="Hapus Penyesuaian Stok"
                description="Semua item dalam batch ini akan dihapus. Tindakan ini tidak dapat dibatalkan."
            />
        </div>
    );
};

export default StockAdjustmentData;
